'use client';

import { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { PIPELINE_COLUMNS } from '@/components/common/CustomerCardPanel';
import type { CustomerCard } from '@/components/common/CustomerCardPanel';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

interface AddCustomerModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /** When set, the modal edits this existing card instead of creating a new one. */
  editCard?: CustomerCard | null;
}

// Build the form state from an existing card (edit mode).
function fromCard(c: CustomerCard) {
  return {
    first_name: c.customerFirstName || '', last_name: c.customerLastName || '', display_name: c.displayName || '',
    email: c.email || '', phone_type: c.phoneType || 'Mobile', phone: c.phone || '', phone_ext: c.phoneExt || '',
    location: c.location || 'NextRoof 360', job_type: c.jobType || 'Retail', stage: c.stage || 'new-lead',
    lead_source: c.referralSource || '', sales_rep: c.vpmName || '', assigned_to: c.assignedTo || '',
    address: c.address || '', address_line2: c.addressLine2 || '', city: c.city || '', state: c.state || '', zip: c.zip || '', country: c.country || 'United States',
    job_scheduled_date: c.jobScheduledDate || '', job_time: c.jobTime || '', gated_community: c.gatedCommunity ? 'Yes' : 'No', gate_code: c.gateCode || '',
    date_of_loss: c.dateOfLoss || '', insurance_company: c.insuranceCompany || '', claim_number: c.claimNumber || '', policy_number: c.policyNumber || '',
    adjuster_name: c.adjusterName || '', adjuster_phone: c.adjusterPhone || '', adjuster_email: c.adjusterEmail || '',
    outside_adjuster: c.outsideAdjuster || '', outside_adjuster_email: c.outsideAdjusterEmail || '', outside_adjuster_number: c.outsideAdjusterNumber || '',
    salesrabbit_id: c.salesrabbitId || '', company: c.company || '', website: c.website || '',
  };
}

const NAVY = '#1C2E5E';
const ORANGE = '#E89A3C';

const emptyForm = {
  // Contact
  first_name: '', last_name: '', display_name: '',
  email: '', phone_type: 'Mobile', phone: '', phone_ext: '',
  location: 'NextRoof 360', job_type: 'Retail', stage: 'new-lead',
  lead_source: '', sales_rep: '', assigned_to: '',
  // Address
  address: '', address_line2: '', city: '', state: '', zip: '', country: 'United States',
  // Custom fields
  job_scheduled_date: '', job_time: '', gated_community: 'No', gate_code: '',
  date_of_loss: '', insurance_company: '', claim_number: '', policy_number: '',
  adjuster_name: '', adjuster_phone: '', adjuster_email: '',
  outside_adjuster: '', outside_adjuster_email: '', outside_adjuster_number: '',
  salesrabbit_id: '',
  // Other
  company: '', website: '',
};

// Shared class strings + field components live at MODULE scope. Defining these
// inside the component would give them a new identity on every keystroke, which
// remounts each <input> and drops focus after one character.
const inp = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/40 focus:border-[#E89A3C] placeholder:text-gray-400';
const label = 'block text-xs font-semibold text-gray-600 mb-1.5';

function Field({
  lab, k, placeholder, type = 'text', f, set,
}: {
  lab: string;
  k: keyof typeof emptyForm;
  placeholder?: string;
  type?: string;
  f: typeof emptyForm;
  set: (k: keyof typeof emptyForm, v: string) => void;
}) {
  return (
    <div>
      <label className={label}>{lab}</label>
      <input type={type} value={f[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder} className={inp} />
    </div>
  );
}

// Type-ahead picker for any user in the system. Typing filters the list; the
// underlying value is still free text (f[k]) so a name that isn't a known user
// is preserved. Module-scoped so it keeps input focus across re-renders.
function UserPicker({
  lab, k, placeholder, f, set, users, loading,
}: {
  lab: string;
  k: keyof typeof emptyForm;
  placeholder?: string;
  f: typeof emptyForm;
  set: (k: keyof typeof emptyForm, v: string) => void;
  users: { id: string; name: string }[];
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = (f[k] || '').toLowerCase();
  const filtered = q ? users.filter(u => u.name.toLowerCase().includes(q)) : users;

  return (
    <div ref={ref} className="relative">
      <label className={label}>{lab}</label>
      <input
        value={f[k]}
        onChange={e => { set(k, e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={inp}
        autoComplete="off"
      />
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto z-20">
          {loading ? (
            <div className="px-3 py-2 text-xs text-gray-400">Loading users…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">{users.length === 0 ? 'No users found' : 'No matches'}</div>
          ) : (
            filtered.map(u => (
              <button
                key={u.id}
                type="button"
                onClick={() => { set(k, u.name); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-[11px] font-bold flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </span>
                {u.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Google Places address autocomplete for Address Line 1. Typing (debounced)
// shows suggestions; picking one fills street / city / state / zip. Free text is
// still preserved if the user doesn't pick a suggestion.
function AddressAutocomplete({
  f, set, setMany,
}: {
  f: typeof emptyForm;
  set: (k: keyof typeof emptyForm, v: string) => void;
  setMany: (patch: Partial<typeof emptyForm>) => void;
}) {
  const [preds, setPreds] = useState<{ description: string; place_id: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRef = useRef<string>('');

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onType = (v: string) => {
    set('address', v);
    setOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (v.trim().length < 3) { setPreds([]); return; }
    if (!tokenRef.current) {
      tokenRef.current = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(v.length);
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/places/autocomplete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input: v, sessionToken: tokenRef.current }),
        });
        const data = await res.json();
        setPreds(data?.predictions || []);
      } catch {
        setPreds([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const pick = async (p: { description: string; place_id: string }) => {
    setOpen(false);
    setPreds([]);
    try {
      const res = await fetch('/api/places/details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ place_id: p.place_id, sessionToken: tokenRef.current }),
      });
      const d = await res.json();
      if (d?.success) {
        setMany({
          address: d.street || p.description,
          city: d.city || f.city,
          state: d.state || f.state,
          zip: d.zip || f.zip,
          ...(d.country ? { country: d.country } : {}),
        });
      } else {
        set('address', p.description);
      }
    } catch {
      set('address', p.description);
    }
    tokenRef.current = ''; // end the billing session
  };

  return (
    <div ref={ref} className="relative">
      <label className={label}>Address Line 1</label>
      <input
        value={f.address}
        onChange={e => onType(e.target.value)}
        onFocus={() => { if (preds.length) setOpen(true); }}
        placeholder="Start typing an address…"
        className={inp}
        autoComplete="off"
      />
      {open && (loading || preds.length > 0) && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-20">
          {loading && preds.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>
          ) : (
            preds.map(p => (
              <button
                key={p.place_id}
                type="button"
                onClick={() => pick(p)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-start gap-2"
              >
                <Icon name="MapPinIcon" size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <span>{p.description}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SectionHead({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 bg-gray-50 rounded-t-xl">
      <Icon name={icon as any} size={17} style={{ color: NAVY }} />
      <h3 className="text-[15px] font-bold" style={{ color: NAVY }}>{title}</h3>
    </div>
  );
}

export default function AddCustomerModal({ onClose, onCreated, editCard }: AddCustomerModalProps) {
  const supabase = createClient();
  const isEdit = !!editCard;
  const [f, setF] = useState(editCard ? fromCard(editCard) : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof emptyForm, v: string) => setF(prev => ({ ...prev, [k]: v }));
  const setMany = (patch: Partial<typeof emptyForm>) => setF(prev => ({ ...prev, ...patch }));

  // Any user in the system — powers the Sales rep / Assigned type-ahead pickers.
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoadingUsers(true);
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name')
          .order('first_name')
          .abortSignal(controller.signal);
        if (data) {
          setUsers(
            data
              .map((u: any) => ({ id: u.id, name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() }))
              .filter((u: any) => u.name),
          );
        }
      } catch {
        /* aborted or failed — pickers just show "No users found" */
      } finally {
        setLoadingUsers(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!f.first_name.trim()) { setError('First name / Company name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        customer_first_name: f.first_name.trim(),
        customer_last_name: f.last_name.trim() || null,
        display_name: f.display_name.trim() || null,
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        phone_type: f.phone_type || null,
        phone_ext: f.phone_ext.trim() || null,
        location: f.location.trim() || null,
        job_type: f.job_type || null,
        referral_source: f.lead_source.trim() || null,
        vpm_name: f.sales_rep.trim() || null,
        assigned_to: f.assigned_to.trim() || null,
        address: f.address.trim() || null,
        address_line2: f.address_line2.trim() || null,
        city: f.city.trim() || null,
        state: f.state || null,
        zip: f.zip.trim() || null,
        country: f.country || null,
        job_scheduled_date: f.job_scheduled_date || null,
        job_time: f.job_time.trim() || null,
        gated_community: f.gated_community === 'Yes',
        gate_code: f.gate_code.trim() || null,
        date_of_loss: f.date_of_loss || null,
        insurance_company: f.insurance_company.trim() || null,
        claim_number: f.claim_number.trim() || null,
        policy_number: f.policy_number.trim() || null,
        adjuster_name: f.adjuster_name.trim() || null,
        adjuster_phone: f.adjuster_phone.trim() || null,
        adjuster_email: f.adjuster_email.trim() || null,
        outside_adjuster: f.outside_adjuster.trim() || null,
        outside_adjuster_email: f.outside_adjuster_email.trim() || null,
        outside_adjuster_number: f.outside_adjuster_number.trim() || null,
        salesrabbit_id: f.salesrabbit_id.trim() || null,
        company: f.company.trim() || null,
        website: f.website.trim() || null,
        stage: f.stage,
      };

      if (isEdit && editCard) {
        // Preserve time-in-stage unless the stage actually changed.
        const extra: any = { updated_at: new Date().toISOString() };
        if (editCard.stage !== f.stage) extra.column_entered_at = new Date().toISOString();
        const { error: err } = await supabase.from('customer_pipeline').update({ ...payload, ...extra }).eq('id', editCard.id);
        if (err) throw err;
      } else {
        // Array-form insert (no .single() — see CLAUDE.md). job_id auto-assigned by trigger.
        const { error: err } = await supabase.from('customer_pipeline').insert({ ...payload, column_entered_at: new Date().toISOString() });
        if (err) throw err;
      }
      onCreated?.();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to add customer.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>{isEdit ? 'Edit Customer' : 'New Customer'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><Icon name="XMarkIcon" size={20} /></button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5" style={{ background: '#FBFBFC' }}>
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">{error}</div>}

          {/* Contact */}
          <div className="border border-gray-200 rounded-xl bg-white">
            <SectionHead icon="UserIcon" title="Contact" />
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Enter the customer’s contact details. First name / Company name is required.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={label}>First name / Company name <span className="text-red-500">*</span></label>
                  <input value={f.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Enter first name or company" className={inp} />
                </div>
                <Field f={f} set={set} lab="Last name" k="last_name" placeholder="Enter last name" />
              </div>
              <div>
                <label className={label}>Display name</label>
                <input value={f.display_name} onChange={e => set('display_name', e.target.value)} placeholder="Unique identifier for this contact" className={inp} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field f={f} set={set} lab="Email" k="email" placeholder="Enter email address" type="email" />
                <div>
                  <label className={label}>Phone</label>
                  <div className="flex gap-2">
                    <select value={f.phone_type} onChange={e => set('phone_type', e.target.value)} className={`${inp} w-28`}>
                      <option>Mobile</option><option>Home</option><option>Work</option>
                    </select>
                    <input value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 555-5555" className={inp} />
                    <input value={f.phone_ext} onChange={e => set('phone_ext', e.target.value)} placeholder="Ext" className={`${inp} w-20`} />
                  </div>
                </div>
              </div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 pt-1">More Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field f={f} set={set} lab="Location" k="location" />
                <div>
                  <label className={label}>Type</label>
                  <select value={f.job_type} onChange={e => set('job_type', e.target.value)} className={inp}>
                    <option>Retail</option><option>Insurance</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Status</label>
                  <select value={f.stage} onChange={e => set('stage', e.target.value)} className={inp}>
                    {PIPELINE_COLUMNS.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>
                <Field f={f} set={set} lab="Lead source" k="lead_source" placeholder="Select lead source" />
                <UserPicker f={f} set={set} users={users} loading={loadingUsers} lab="Sales rep" k="sales_rep" placeholder="Search users…" />
                <UserPicker f={f} set={set} users={users} loading={loadingUsers} lab="Assigned" k="assigned_to" placeholder="Search users…" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="border border-gray-200 rounded-xl bg-white">
            <SectionHead icon="MapPinIcon" title="Address" />
            <div className="p-5 space-y-4">
              <AddressAutocomplete f={f} set={set} setMany={setMany} />
              <Field f={f} set={set} lab="Address Line 2" k="address_line2" placeholder="Apt, suite, unit, etc." />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field f={f} set={set} lab="City" k="city" placeholder="Enter city" />
                <div>
                  <label className={label}>State</label>
                  <select value={f.state} onChange={e => set('state', e.target.value)} className={inp}>
                    <option value="">Select</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Field f={f} set={set} lab="Zip Code" k="zip" placeholder="Enter zip code" />
                <Field f={f} set={set} lab="Country" k="country" />
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          <div className="border border-gray-200 rounded-xl bg-white">
            <SectionHead icon="RectangleStackIcon" title="Custom Fields" />
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field f={f} set={set} lab="Job Scheduled" k="job_scheduled_date" type="date" />
              <Field f={f} set={set} lab="Job Time" k="job_time" />
              <div>
                <label className={label}>Gated Community</label>
                <select value={f.gated_community} onChange={e => set('gated_community', e.target.value)} className={inp}>
                  <option>No</option><option>Yes</option>
                </select>
              </div>
              <Field f={f} set={set} lab="Gate Code" k="gate_code" />
              <Field f={f} set={set} lab="Date of Loss" k="date_of_loss" type="date" />
              <Field f={f} set={set} lab="Insurance Company" k="insurance_company" />
              <Field f={f} set={set} lab="Claim #" k="claim_number" />
              <Field f={f} set={set} lab="Policy #" k="policy_number" />
              <Field f={f} set={set} lab="Adjuster’s Name" k="adjuster_name" />
              <Field f={f} set={set} lab="Adjuster’s Phone #" k="adjuster_phone" />
              <Field f={f} set={set} lab="Adjuster’s Email" k="adjuster_email" />
              <Field f={f} set={set} lab="Outside Adjuster" k="outside_adjuster" />
              <Field f={f} set={set} lab="Outside Adjuster Email" k="outside_adjuster_email" />
              <Field f={f} set={set} lab="Outside Adjuster #" k="outside_adjuster_number" />
              <Field f={f} set={set} lab="SalesRabbit ID" k="salesrabbit_id" />
            </div>
          </div>

          {/* Other Info */}
          <div className="border border-gray-200 rounded-xl bg-white">
            <SectionHead icon="Cog6ToothIcon" title="Other Info" />
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field f={f} set={set} lab="Company" k="company" placeholder="Enter company name" />
              <Field f={f} set={set} lab="Website" k="website" placeholder="www.example.com" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 hover:opacity-90"
            style={{ background: NAVY }}>
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="CheckIcon" size={16} variant="solid" />}
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save Customer')}
          </button>
        </div>
      </div>
    </div>
  );
}
